import { Component, DestroyRef, effect, inject, signal, untracked } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, forkJoin } from 'rxjs';
import { AnalyticsService } from '../../core/services/analytics.service';
import { DateRangeService } from '../../core/services/date-range.service';
import { HotspotListResponse, RepositoryHotspotRanking } from '../../core/models/hotspot.models';

@Component({
  selector: 'app-hotspots-page',
  standalone: true,
  imports: [],
  templateUrl: './hotspots-page.component.html',
  styleUrl: './hotspots-page.component.scss',
})
export class HotspotsPageComponent {
  private readonly analyticsService = inject(AnalyticsService);
  private readonly dateRange = inject(DateRangeService);
  private readonly destroyRef = inject(DestroyRef);
  private currentLoadId = 0;

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly fileHotspots = signal<HotspotListResponse | null>(null);
  readonly repositoryHotspots = signal<RepositoryHotspotRanking | null>(null);
  readonly groupBy = signal<'file' | 'folder'>('file');

  constructor() {
    effect(() => {
      this.dateRange.appliedRange();
      this.dateRange.appliedRevision();
      untracked(() => this.load());
    });
  }

  load(): void {
    const loadId = ++this.currentLoadId;
    this.loading.set(true);
    this.error.set(null);
    this.fileHotspots.set(null);
    this.repositoryHotspots.set(null);

    const params = this.dateRange.toQueryParams();

    forkJoin({
      file: this.analyticsService.getHotspots({ ...params, groupBy: this.groupBy(), top: 20 }),
      repos: this.analyticsService.getRepositoryHotspots({ ...params, top: 10 }),
    })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          if (loadId === this.currentLoadId) this.loading.set(false);
        }),
      )
      .subscribe({
        next: ({ file, repos }) => {
          if (loadId !== this.currentLoadId) return;
          this.fileHotspots.set(file);
          this.repositoryHotspots.set(repos);
        },
        error: (err: unknown) => {
          if (loadId !== this.currentLoadId) return;
          console.error('Hotspots request failed', err);
          this.error.set(this.formatError(err, 'Hotspots data could not be loaded.'));
        },
      });
  }

  switchGroupBy(value: 'file' | 'folder'): void {
    this.groupBy.set(value);
    this.load();
  }

  maxIssueCount(hotspots: HotspotListResponse): number {
    return Math.max(...hotspots.items.map((h) => h.issueCount), 1);
  }

  barWidth(count: number, max: number): string {
    return `${Math.round((count / max) * 100)}%`;
  }

  private formatError(error: unknown, fallback: string): string {
    if ((error as { name?: string })?.name === 'TimeoutError') return 'Request timed out after 8s.';
    if (error instanceof HttpErrorResponse) {
      if (error.status === 0) return `Cannot reach backend${error.url ? ` @ ${error.url}` : ''}.`;
      return `${fallback} (${error.status}: ${error.message})`;
    }
    return fallback;
  }
}
